import { Edit, LockPersonOutlined } from "@mui/icons-material";
import { Box } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { getMyProfile } from "../../actions/user";
import { UserActivities } from "../../components/administration";
import { ProfileCard, ProfileDetails, ResetPassword, UpdateCompany, UpdateCompanyLogoModal } from "../../components/my-profile";
import { PageHeader } from "../../components/v3";
import { USER_TYPE } from "../../constants";
import { useCompanyLogo } from "../../hooks/useCompanyLogo";
import { PageLayout } from "../../layouts/PageLayout";
import { CompanyService } from "../../services";

export const MyProfile = () => {
  const { roundedLogo } = useCompanyLogo();

  const [companyDetails, setCompanyDetails] = useState(null);
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [openCompanyModal, setOpenCompanyModal] = useState(false);
  const [openCompanyLogoModal, setOpenCompanyLogoModal] = useState(false);
  const [reload, setReload] = useState(0);

  const dispatch = useDispatch();
  const userDetails = useSelector((state) => state.user.user);

  // TODO: TBD to remove useCallback
  const fetchData = useCallback(async () => {
    // TODO: TBD to remove dispatch call from here
    const response = await dispatch(getMyProfile());
    if (!response) {
      toast.error("Failed to fetch user data");
      return;
    }

    const { data: company } = await CompanyService.getCompanyDetails(response?.data?.company?.id);
    setCompanyDetails(company);
  }, [dispatch]);

  // TODO: TBD to remove fetchData dependency
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const locality = useMemo(() => {
    return [userDetails?.company?.primaryAddress?.locality, userDetails?.company?.primaryAddress?.state].filter(Boolean);
  }, [userDetails?.company?.primaryAddress?.locality, userDetails?.company?.primaryAddress?.state]);

  const handleOpenResetPassword = () => setOpenPasswordModal(true);
  const handleClosePasswordModal = () => setOpenPasswordModal(false);

  const handleOpenUpdateCompany = () => setOpenCompanyModal(true);
  const handleCloseCompanyModal = () => setOpenCompanyModal(false);

  const handleOpenCompanyLogoModal = () => setOpenCompanyLogoModal(true);
  const handleCloseCompanyLogoModal = () => setOpenCompanyLogoModal(false);

  const handleReload = () => setReload((prev) => prev + 1);

  const handleUpdateCompanyLogo = () => handleOpenCompanyLogoModal();

  const isAdmin = userDetails?.type === USER_TYPE.ADMIN;

  const userActions = isAdmin
    ? [{ buttonText: "Reset Password", buttonIcon: LockPersonOutlined, onClick: handleOpenResetPassword }]
    : [];

  const companyActions = isAdmin ? [{ buttonText: "Edit", buttonIcon: Edit, onClick: handleOpenUpdateCompany }] : [];

  return (
    <>
      <PageLayout>
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <PageHeader
            // title
            title="My Profile"
          />
        </Box>

        <Box sx={{ mt: -9, zIndex: 2, position: "relative" }}>
          <ProfileCard userDetails={userDetails} />
        </Box>

        <ProfileDetails
          defaultExpanded={isAdmin}
          title="User Information"
          buttons={userActions}
          rows={[
            [
              { label: "First Name", value: userDetails?.firstName },
              { label: "Last Name", value: userDetails?.lastName },
              { label: "User Name", value: userDetails?.username },
            ],
            [
              { label: "Email Address", value: userDetails?.primaryEmail },
              { label: "Phone Number", value: userDetails?.primaryPhone },
              { label: "User Type", value: userDetails?.type },
            ],
          ]}
        />

        <ProfileDetails
          defaultExpanded={isAdmin}
          title="Company Information"
          avatarSrc={roundedLogo}
          avatarTooltip={isAdmin ? "Update company logo" : ""}
          updateProfilePhoto={isAdmin ? handleUpdateCompanyLogo : null}
          buttons={companyActions}
          rows={[
            // TODO: make single source of truth for company data
            [
              { label: "Company Name", value: userDetails?.company?.name },
              { label: "Phone Number", value: userDetails?.company?.primaryPhone },
              { label: "Email Address", value: userDetails?.company?.primaryEmail },
            ],
            [
              { label: "GST Number", value: companyDetails?.taxDetails?.gstNumber },
              { label: "PAN Number", value: companyDetails?.taxDetails?.panNumber },
            ],
            [
              { label: "Place", value: locality.join(", ") },
              { label: "Pin code", value: userDetails?.company?.primaryAddress?.pinCode },
              { label: "Country", value: userDetails?.company?.primaryAddress?.country },
            ],
          ]}
        />

        {userDetails?.type === USER_TYPE.USER && (
          <UserActivities
            userId={userDetails?.id}
            accountId={userDetails?.accounts?.[0]?.id}
            onSuccess={handleReload}
            reloadData={reload}
          />
        )}
      </PageLayout>

      {/* Change Password Modal */}
      <ResetPassword userId={userDetails.id} open={openPasswordModal} onClose={handleClosePasswordModal} />

      {/* Update Company Details */}
      <UpdateCompany
        companyId={userDetails?.company?.id}
        open={openCompanyModal}
        onClose={handleCloseCompanyModal}
        onSuccess={fetchData}
      />

      {/* Update Company Logo */}
      <UpdateCompanyLogoModal
        companyId={userDetails?.company?.id}
        open={openCompanyLogoModal}
        onClose={handleCloseCompanyLogoModal}
        onSuccess={fetchData}
      />
    </>
  );
};
